<template>
    <div class="manifest-review">
        <b-alert v-if="buildIssues.length" variant="danger" show class="mb-2">
            <div class="alert-body">
                <div class="font-weight-bold mb-75">
                    Complete these items before creating a version
                </div>
                <div
                    v-for="item in buildIssues"
                    :key="`${item.code}:${item.path}`"
                    class="manifest-review__build-issue"
                >
                    <span>{{ item.message }}</span>
                    <b-button
                        size="sm"
                        variant="flat-danger"
                        @click="$emit('edit-step', item.step)"
                    >
                        Edit {{ stepTitle(item.step) }}
                    </b-button>
                </div>
            </div>
        </b-alert>

        <b-alert v-else variant="success" show class="mb-2">
            <div class="alert-body d-flex align-items-center">
                <feather-icon icon="CheckCircleIcon" size="18" class="mr-75" />
                The definition is structurally ready to be submitted for backend validation.
            </div>
        </b-alert>

        <div class="manifest-review__summary mb-2">
            <article v-for="item in summary" :key="item.title" class="manifest-review__summary-item">
                <small class="text-muted text-uppercase">{{ item.title }}</small>
                <div class="font-weight-bold mt-25">{{ item.value }}</div>
                <div class="text-muted font-small-2 mt-25">{{ item.detail }}</div>
            </article>
        </div>

        <section v-if="versionResult" class="manifest-review__result mb-2">
            <div class="d-flex flex-wrap align-items-center justify-content-between mb-1">
                <div>
                    <h4 class="mb-25">Revision {{ versionResult.revision }} created</h4>
                    <div class="text-muted">
                        The immutable version was saved and was not activated automatically.
                    </div>
                </div>
                <div>
                    <b-badge
                        :variant="versionResult.activatable ? 'light-success' : 'light-danger'"
                        class="mr-50"
                    >
                        {{ versionResult.activatable ? 'Activatable' : 'Not activatable' }}
                    </b-badge>
                    <b-badge
                        :variant="versionResult.compilationFingerprint ? 'light-success' : 'light-secondary'"
                        class="mr-50"
                    >
                        {{ versionResult.compilationFingerprint ? 'Compiled' : 'Not compiled' }}
                    </b-badge>
                    <b-badge v-if="versionResult.validationErrorCount" variant="light-danger" class="mr-50">
                        {{ versionResult.validationErrorCount }} errors
                    </b-badge>
                    <b-badge v-if="versionResult.validationWarningCount" variant="light-warning">
                        {{ versionResult.validationWarningCount }} warnings
                    </b-badge>
                </div>
            </div>

            <div v-if="validationGroups.length" class="manifest-review__validation-groups">
                <article
                    v-for="group in validationGroups"
                    :key="group.path"
                    class="manifest-review__validation-group"
                >
                    <div class="d-flex align-items-center justify-content-between mb-75">
                        <code>{{ group.path }}</code>
                        <b-button
                            v-if="stepForPath(group.path)"
                            size="sm"
                            variant="flat-primary"
                            @click="$emit('edit-step', stepForPath(group.path))"
                        >
                            Edit {{ stepTitle(stepForPath(group.path)) }}
                        </b-button>
                    </div>
                    <div
                        v-for="validationIssue in group.issues"
                        :key="`${validationIssue.severity}:${validationIssue.code}:${validationIssue.message}`"
                        class="manifest-review__validation-issue"
                    >
                        <b-badge
                            :variant="validationIssue.severity === 'warning' ? 'light-warning' : 'light-danger'"
                        >
                            {{ validationIssue.severity }}
                        </b-badge>
                        <div>
                            <div>{{ validationIssue.message }}</div>
                            <code>{{ validationIssue.code }}</code>
                        </div>
                    </div>
                </article>
            </div>

            <div v-else class="manifest-review__valid-result text-success">
                <feather-icon icon="CheckIcon" size="17" class="mr-50" />
                Backend validation completed without issues.
            </div>
        </section>

        <section class="manifest-review__definition">
            <div class="d-flex align-items-center justify-content-between">
                <div>
                    <h5 class="mb-25">Generated definition</h5>
                    <div class="text-muted">This exact JSON will be sent to the backend.</div>
                </div>
                <b-button size="sm" variant="outline-secondary" @click="showDefinition = !showDefinition">
                    {{ showDefinition ? 'Hide JSON' : 'Show JSON' }}
                </b-button>
            </div>
            <pre v-if="showDefinition" class="manifest-review__json mb-0">{{ formattedDefinition }}</pre>
        </section>
    </div>
</template>

<script>
import {
    BAlert,
    BBadge,
    BButton,
} from 'bootstrap-vue'

const STEP_TITLES = {
    source: 'Source',
    product: 'Import behavior',
    mapping: 'Field mapping',
    planning: 'Planning',
}

export default {
    name: 'ManifestReviewStep',
    components: {
        BAlert,
        BBadge,
        BButton,
    },
    props: {
        definition: {
            type: Object,
            required: true,
        },
        buildIssues: {
            type: Array,
            default: () => [],
        },
        versionResult: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            showDefinition: false,
        }
    },
    computed: {
        sourceTab() {
            const tabs = this.definition.source && this.definition.source.tabs
                ? Object.values(this.definition.source.tabs)
                : []
            return tabs[0] || {}
        },
        mappedTab() {
            const tabs = this.definition.mapping && this.definition.mapping.tabs
                ? Object.values(this.definition.mapping.tabs)
                : []
            return tabs[0] || {fields: {}}
        },
        summary() {
            const selector = this.sourceTab.selector
            const sourceName = selector && selector.arguments && selector.arguments.name
                ? selector.arguments.name
                : 'Not configured'
            const fields = this.mappedTab.fields || {}
            const recordSteps = Array.isArray(this.mappedTab.recordPipeline)
                ? this.mappedTab.recordPipeline.length
                : 0

            return [
                {
                    title: 'Source',
                    value: sourceName,
                    detail: `Header row ${this.sourceTab.headerRow || '—'} · data row ${this.sourceTab.dataStartRow || '—'}`,
                },
                {
                    title: 'Mapping',
                    value: `${Object.keys(fields).length} fields`,
                    detail: `${recordSteps} record pipeline steps`,
                },
                {
                    title: 'Target',
                    value: this.definition.target.driver || 'Not configured',
                    detail: this.definition.target.driverVersion
                        ? `Version ${this.definition.target.driverVersion}`
                        : 'Version —',
                },
                {
                    title: 'Planning',
                    value: this.definition.planning.policy || 'Not configured',
                    detail: this.definition.planning.policyVersion
                        ? `Version ${this.definition.planning.policyVersion}`
                        : 'Version —',
                },
            ]
        },
        validationIssues() {
            return this.versionResult
                && this.versionResult.validationReport
                && Array.isArray(this.versionResult.validationReport.issues)
                ? this.versionResult.validationReport.issues
                : []
        },
        validationGroups() {
            const groups = this.validationIssues.reduce((result, validationIssue) => {
                const path = validationIssue.path || '/'
                if (!result[path]) {
                    result[path] = []
                }
                result[path].push(validationIssue)
                return result
            }, {})

            return Object.entries(groups).map(([path, issues]) => ({
                path,
                issues: [...issues].sort((left, right) => (
                    left.severity === right.severity
                        ? 0
                        : left.severity === 'error' ? -1 : 1
                )),
            }))
        },
        formattedDefinition() {
            return JSON.stringify(this.definition, null, 2)
        },
    },
    methods: {
        stepTitle(step) {
            return STEP_TITLES[step] || step
        },
        stepForPath(path) {
            if (path.startsWith('/source')) {
                return 'source'
            }
            if (path.startsWith('/mapping')) {
                return 'mapping'
            }
            if (path.startsWith('/target')) {
                return 'product'
            }
            if (path.startsWith('/planning')) {
                return 'planning'
            }
            return null
        },
    },
}
</script>

<style lang="scss" scoped>
.manifest-review__build-issue,
.manifest-review__validation-issue {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
}

.manifest-review__build-issue + .manifest-review__build-issue {
    margin-top: .5rem;
}

.manifest-review__summary {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: .75rem;
}

.manifest-review__summary-item,
.manifest-review__definition,
.manifest-review__result {
    padding: 1rem;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
    background: #fff;
}

.manifest-review__validation-groups {
    display: grid;
    gap: .75rem;
}

.manifest-review__validation-group {
    padding: .75rem;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
}

.manifest-review__validation-issue {
    justify-content: flex-start;
    align-items: flex-start;
}

.manifest-review__validation-issue + .manifest-review__validation-issue {
    margin-top: .75rem;
}

.manifest-review__valid-result {
    display: flex;
    align-items: center;
    padding: .75rem;
    border-radius: .357rem;
    background: rgba(40, 199, 111, .08);
}

.manifest-review__json {
    overflow: auto;
    max-height: 32rem;
    margin-top: 1rem;
    padding: 1rem;
    border-radius: .357rem;
    color: #ebe9f1;
    background: #2f2d38;
    font-size: .8rem;
    white-space: pre;
}

@media (max-width: 1199.98px) {
    .manifest-review__summary {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 575.98px) {
    .manifest-review__summary {
        grid-template-columns: 1fr;
    }
}
</style>

